import {lazy} from '@aboutyou/lazy-imports';
import React, {FC} from 'react';
import {SearchSuggestCampaignsFetcher} from './SearchSuggestCampaignsFetcher';
import {recentlySearchedLabelsInterface} from './SearchSuggestionsHistory';
import {SearchSuggestionsProps} from './SearchSuggestionsView';
import {useGetSearchSuggestCampaignsRequest} from './useGetSearchSuggestCampaignsRequest';

const SearchSuggestions = lazy(() =>
  import('./SearchSuggestions.eager').then(mod => ({
    default: mod.SearchSuggestions,
  })),
);
const SearchSuggestionsHistory = lazy(() =>
  import('./SearchSuggestionsHistory').then(mod => ({
    default: mod.SearchSuggestionsHistory,
  })),
);

export const MIN_CHARS_TO_SEARCH = 2;

interface ConditionalSearchSuggestionsHistoryProps {
  search: string;
  handleRemoveInputFocus: VoidFunction;
  isInputFocused: boolean;
  renderSponsoredSlider?: boolean;
}

export const ConditionalSearchSuggestionsHistory: FC<
  ConditionalSearchSuggestionsHistoryProps &
    Omit<SearchSuggestionsProps, 'className' | 'onClick'> &
    recentlySearchedLabelsInterface
> = ({
  isInputFocused,
  search,
  handleRemoveInputFocus,
  searchBarRef,
  isSearchBarExpanded,
  createSearchPath,
  onClickSuggestion,
  shouldDisableSearchBarResults,
  disableKeyNavigation,
  enableRightArrow,
  recentlySearchedLabels,
  onNavigateSuggestionList,
  enableSearchSuggestionsFocusOnTab,
  renderSponsoredSlider = true,
}) => {
  const searchSuggestCampaignsRequest = useGetSearchSuggestCampaignsRequest();
  const hasSponsoredCampaigns = Boolean(
    searchSuggestCampaignsRequest.data?.section?.length,
  );
  const sponsoredTeaserSlot =
    renderSponsoredSlider && hasSponsoredCampaigns ? (
      <SearchSuggestCampaignsFetcher onTeaserClick={handleRemoveInputFocus} />
    ) : null;

  if (!isInputFocused) {
    return null;
  }

  return (
    <>
      {search.length < MIN_CHARS_TO_SEARCH ? (
        <SearchSuggestionsHistory
          searchBarRef={searchBarRef}
          isSearchBarExpanded={isSearchBarExpanded}
          onClick={handleRemoveInputFocus}
          onClickSuggestion={onClickSuggestion}
          createSearchPath={createSearchPath}
          shouldDisableSearchBarResults={shouldDisableSearchBarResults}
          disableKeyNavigation={disableKeyNavigation}
          enableRightArrow={enableRightArrow}
          recentlySearchedLabels={recentlySearchedLabels}
          onNavigateSuggestionList={onNavigateSuggestionList}
          enableSearchSuggestionsFocusOnTab={enableSearchSuggestionsFocusOnTab}
          renderSponsoredSlider={renderSponsoredSlider}
          sponsoredTeaserSlot={sponsoredTeaserSlot}
        />
      ) : (
        <SearchSuggestions
          onClickSuggestion={onClickSuggestion}
          search={search}
          searchBarRef={searchBarRef}
          isSearchBarExpanded={isSearchBarExpanded}
          onClick={handleRemoveInputFocus}
          createSearchPath={createSearchPath}
          shouldDisableSearchBarResults={shouldDisableSearchBarResults}
          disableKeyNavigation={disableKeyNavigation}
          enableRightArrow={enableRightArrow}
          onNavigateSuggestionList={onNavigateSuggestionList}
          enableSearchSuggestionsFocusOnTab={enableSearchSuggestionsFocusOnTab}
          sponsoredTeaserSlot={sponsoredTeaserSlot}
        />
      )}
    </>
  );
};
